import { NgClass, NgPlural, NgPluralCase } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, forwardRef, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IRelatedProductsOptions } from '@df/catalog-ui/related-products/related-products.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsOutletBlockComponent } from '../catalog/outlet-block/icms-outlet-block.component';
import { FlushBasketButtonComponent } from '../catalog/ui/basket/flush-basket-button/flush-basket-button';
import { IfStoreDirective } from '../multisite/if-store.directive';
import { ModalLayoutRightComponent, NModalLayoutRight } from './../ui/modal/modal-layout-right.component';
import { MinibasketInitiatorLocalService } from './minibasket-initiatior-local.service';
import { MinibasketItemComponent } from './minibasket-item.component';
import { PaymentMethodsLogosBlockComponent } from './payments-methods-block/payments-methods-logos-block.component';
import { BasketRelatedProductsComponent } from './related-products/basket-related-products.component';
import { BasketViewTotalsComponent } from './totals/basket-view-totals.component';
import { IfTradeStoreDirective } from '../multisite/trade/if-trade-store.directive';
import { LocalBasketItem } from '../customer/basket/basket-item.decorator';
import { LocalBasket } from './local-basket';

export namespace NMinibasket {
    export const minibasketOpenEvent = 'minibasket-open';
    export interface IMinibasketData extends Catalog.IBlockData<IContent> {}

    export interface IContent {
        delivery_info: string;
        klevu_recommendations_id?: string;
    }
}
export const minibasketModalId = 'minibasket';
@DfCmsModal({
    id: minibasketModalId,
    position: 'right',
    name: 'Minibasket',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: '31.7rem'
    }),
    initiators: [MinibasketInitiatorLocalService],
    sizeRestriction: ['X', 'L'],
    reopenPolicy: NDfModal.ReopenPolicy.first,
    prerenderAllowed: false,
    forbiddenLocation: /^\/(basket|checkout)$/
})
@ICmsComponent<MinibasketComponent>({
    id: minibasketModalId,
    name: 'Minibasket Popup',
    block: {
        url: minibasketModalId,
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IField>{
            key: 'delivery_info',
            type: NICmsContentField.EFieldType.text,
            title: 'Delivery Info',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'klevu_recommendations_id',
            type: NICmsContentField.EFieldType.text,
            title: 'Klevu Recommendations ID'
        }
    ]
})
@Component({
    selector: 'minibasket',
    templateUrl: './minibasket.html',
    styleUrls: ['minibasket.scss'],
    standalone: true,
    imports: [
        BasketRelatedProductsComponent,
        forwardRef(() => BasketViewTotalsComponent),
        FlushBasketButtonComponent,
        ICmsOutletBlockComponent,
        IfStoreDirective,
        IfTradeStoreDirective,
        MinibasketItemComponent,
        ModalLayoutRightComponent,
        NgClass,
        NgPlural,
        NgPluralCase,
        OrderByPipe,
        PaymentMethodsLogosBlockComponent,
        PriceComponent,
        RouterLink,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MinibasketComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NMinibasket.IContent;

    isSalable!: boolean;

    private app = inject(App);
    private modalRef = inject(ModalRef<any>);
    protected basket = inject(LocalBasket);

    get relatedProductsOptions(): IRelatedProductsOptions {
        return {
            engines: [
                { id: KlevuSearch.contextRecommendationsBasketEngineId, options: { strategyId: this.content?.klevu_recommendations_id } }
            ]
        };
    }
    get productsIds(): number[] {
        return this.basketItems.map(i => i.product!.id);
    }

    protected get basketItems(): LocalBasketItem[] {
        return this.basket.items as LocalBasketItem[];
    }
    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdate()));
        this.onBasketUpdate();
        this.app.events.broadcast(NMinibasket.minibasketOpenEvent);
    }

    private onBasketUpdate() {
        this.isSalable = this.basket.isSalable;
        if (this.initiated) {
            this.detectChanges();
        }
    }

    protected close(): void {
        this.modalRef.close();
    }

    itemTrackFn = (index: number, item: BasketItem) => item.id;
}
